import React from "react";
import { Badge, withStyles } from "@material-ui/core";
import { Menu, ShoppingCartOutlined } from "@material-ui/icons";
import styled from "styled-components";
import { useHistory, Link } from "react-router-dom";
import { Image } from "react-bootstrap";
import SearchIcon from "../assets/img/search_icon.png";
import { mobile } from "../responsive";
import LocationPrompt from "./LocationPrompt";

// Custom green badge for healthcare
const GreenBadge = withStyles({
  badge: {
    backgroundColor: '#4caf50',
    color: 'white',
    fontWeight: 'bold',
  },
})(Badge);

const Container = styled.div`
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: 1000;
  background-color: #E91E63;
  left: 0;
`;

const Wrapper = styled.div`
  width: 100%;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #E91E63;
  position: relative;
  box-sizing: border-box;

  @media only screen and (max-width: 850px) {
    flex-direction: column;
    align-items: stretch;
    padding: 8px 12px 0 12px;
    gap: 8px;
  }
`;

const Logo = styled.div`
  font-weight: bold;
  display: flex;
  align-items: center;
`;

const Left = styled.div`
  flex: 1;
  display: flex;
  align-items: center;

  @media only screen and (max-width: 850px) {
    display: none;
  }
`;

const Right = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 40px;

  @media only screen and (max-width: 850px) {
    display: none;
  }
`;

const MenuItem = styled.div`
  font-size: 14px;
  cursor: pointer;
  margin-left: 25px;
`;

const LocationChip = styled.div`
  display: flex;
  align-items: center;
  margin-right: 14px;
  max-width: 260px;
`;

const Center = styled.div`
  flex: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;

  @media only screen and (max-width: 850px) {
    display: none;
  }
`;

const MobileTopRow = styled.div`
  display: none;

  @media only screen and (max-width: 850px) {
    display: flex;
    align-items: center;
    width: 100%;
  }
`;

const MobileBottomRow = styled.div`
  display: none;

  @media only screen and (max-width: 850px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding-bottom: 8px;
    box-sizing: border-box;
    gap: 6px;
  }
`;

const MobileRight = styled.div`
  display: none;

  @media only screen and (max-width: 850px) {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    flex-shrink: 0;
  }
`;

const MobileLocationBar = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const MobileSearchContainer = styled.div`
  display: none;

  @media only screen and (max-width: 850px) {
    display: flex;
    align-items: stretch;
    border: 1px solid lightgray;
    border-radius: 10px;
    width: 100%;
    min-width: 0;
    overflow: hidden;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: stretch;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-radius: 10px;
  flex: 1;
  padding: 0;
  width: 100%;
  overflow: hidden;
  box-shadow: 0 1px 6px rgba(0,0,0,0.2);
`;

const Input = styled.input`
  flex-grow: 1;
  min-width: 0;
  padding: 10px;
  padding-left: 32px;
  font-size: 14px;
  border: none;
  border-radius: 10px 0 0 10px;
  box-sizing: border-box;
  background: white url(${SearchIcon}) no-repeat 10px center;
  background-repeat: no-repeat;
  background-position: 10px center;
  background-size: 17px;
  margin: 0;
  &:focus {
    outline: none;
  }
  ${mobile({ flexGrow: 1 })}
`;

const SearchButton = styled.button`
  padding: 10px 15px;
  font-size: 12px;
  border: none;
  background-color: #C2185B;
  color: white;
  cursor: pointer;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  box-shadow: -6px 2px 10px rgba(0, 0, 0, 0.55);
  &:focus {
    outline: none;
    background-color: #AD1457;
  }
  &:hover {
    background-color: #AD1457;
  }
`;

const ClickableMenu = styled(Menu)`
  font-size: 34px;
  color: white;
  margin-left: 20px;
  opacity: 0.8;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #fce4ec;
  }

  &:active {
    transform: scale(0.9);
  }

  &:focus {
    outline: 2px solid #fce4ec;
  }
`;

const HealthcareNavBar = ({ vendor, cartCount, onCartClick }) => {
  const history = useHistory();

  const goToProfile = () => {
    history.push("/my_profile");
  };

  return (
    <Container>
      <Wrapper>
        {/* ── Desktop layout ── */}
        <Left>
          <Logo>
            <Link to="/">
              <Image
                src="/lusuku_logo_main.png"
                style={{ width: 90, height: 90, marginBottom: 10, marginRight: 8, imageRendering: "-webkit-optimize-contrast" }}
                alt="Lusuku Logo"
              />
            </Link>
          </Logo>
        </Left>
        <Center>
          <SearchContainer>
            <Input type="search" id="search" placeholder="Search healthcare products and services..." />
            <SearchButton>SEARCH</SearchButton>
          </SearchContainer>
        </Center>
        <Right>
          <LocationChip>
            <LocationPrompt compact />
          </LocationChip>
          <MenuItem onClick={onCartClick}>
            <GreenBadge overlap="rectangular" badgeContent={cartCount}>
              <ShoppingCartOutlined style={{ color: "#f4f8f5ff", fontSize: 34, borderRadius: "50%" }} />
            </GreenBadge>
          </MenuItem>
          <ClickableMenu style={{ fontSize: 34, marginRight: 20 }} onClick={goToProfile} />
        </Right>

        {/* ── Mobile layout ── */}
        <MobileTopRow>
          <Logo>
            <Link to="/">
              <Image
                src="/lusuku_logo_main.png"
                style={{ width: 52, height: 52, marginRight: 8, imageRendering: "-webkit-optimize-contrast" }}
                alt="Lusuku Logo"
              />
            </Link>
          </Logo>
          <MobileRight>
            <MenuItem onClick={onCartClick}>
              <GreenBadge overlap="rectangular" badgeContent={cartCount}>
                <ShoppingCartOutlined style={{ color: "#f4f8f5ff", fontSize: 22 }} />
              </GreenBadge>
            </MenuItem>
            <ClickableMenu style={{ fontSize: 28, marginRight: 0 }} onClick={goToProfile} />
          </MobileRight>
        </MobileTopRow>
        <MobileBottomRow>
          <MobileLocationBar>
            <LocationPrompt compact />
          </MobileLocationBar>
          <MobileSearchContainer>
            <Input type="search" id="search-mobile" placeholder="Search healthcare products and services..." />
            <SearchButton>SEARCH</SearchButton>
          </MobileSearchContainer>
        </MobileBottomRow>
      </Wrapper>
    </Container>
  );
};

export default HealthcareNavBar;


